import { useEffect, type ReactNode } from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { AppRootWrapperBlueprint } from '@backstage/plugin-app-react';
import { TopBar } from './nav/TopBar';
import { TOPBAR_HEIGHT } from './nav/topBarLayout';

const STYLE_ID = 'rafay-topbar-layout-styles';

// TopBar.tsx measures the sidebar's real DOM width so it can start exactly
// where the sidebar ends, but that measurement has a brief window (a few
// animation frames, see useSidebarWidth) where it can be stale — e.g. right
// after a route change remounts things. This makes that window harmless: if
// TopBar and the sidebar ever do overlap for a moment, the sidebar (and its
// logo) must always paint on top, never the other way around. Same stable
// test-id selector TopBar.tsx itself relies on.
const LAYOUT_CSS = `
[data-testid="sidebar-root"] {
  z-index: 1300 !important;
}
`;

function ensureStylesInjected() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = LAYOUT_CSS;
  document.head.appendChild(style);
}

// Backstage's <Sidebar> (see @backstage/core-components' Bar.esm.js) is
// `position: fixed; top: 0; bottom: 0` — it's independent of this wrapper's
// layout entirely (ancestor padding never affects a fixed-position
// descendant). So it naturally stays full-height, unmodified, regardless of
// what TopBar renders — no CSS override needed for that. TopBar itself is
// responsible for not overlapping the sidebar's own x-range (see
// TopBar.tsx, which measures the sidebar's actual rendered width); the
// z-index rule above is just the safety net for the moments that isn't
// perfectly in sync.
function TopBarWrapper({ children }: { children: ReactNode }) {
  useEffect(() => {
    ensureStylesInjected();
  }, []);

  return (
    <>
      <TopBar />
      {/* TopBar is `position: fixed` (occupies no flow space), so without
       * this the content area's normal-flow root would start at y=0, under
       * the bar rather than below it. This only affects the content — the
       * sidebar (fixed) ignores it, per the note above.
       *
       * Backstage's <Page> (rendered as `children`) hard-codes
       * `height: 100vh` on its own root (see @backstage/core-components'
       * Page.esm.js — literally `height: "100vh"`, not relative to its
       * parent). Padding this wrapper's top pushes that already-100vh box
       * that far past the real viewport, so the document itself grows a
       * second, mostly-empty scrollbar alongside Page's own internal one —
       * clipping the overflow here instead would just hide that same
       * amount of Page's content, which is worse. The actual fix is the
       * `BackstagePage.root.height` override in rafayTheme.ts, which
       * shrinks Page's own height to exactly account for TOPBAR_HEIGHT so
       * its one internal overflow-y:auto is correct and sufficient. */}
      <div style={{ paddingTop: TOPBAR_HEIGHT }}>{children}</div>
    </>
  );
}

export const topBarModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    AppRootWrapperBlueprint.make({
      name: 'top-bar',
      params: { component: TopBarWrapper },
    }),
  ],
});
